import { useEffect, useState } from "react";
import { apiJson } from "../../utils/api";
import { CoupleCodeBar, Disclaimer } from "./MarriageLayout";
import { ensureCoupleFromAccount, getCoupleCode, getUserId, setCoupleCode } from "../../utils/coupleSession";
import useSeo from "../../hooks/useSeo";

export default function MarriageQuestions() {
  const [code, setCode] = useState(getCoupleCode());
  const [side, setSide] = useState("male");
  const [questions, setQuestions] = useState([]);
  const [progress, setProgress] = useState(null);
  const [report, setReport] = useState(null);
  const [hint, setHint] = useState("");

  useSeo({
    title: "婚前沟通题",
    metaDescription: "双方分别作答金钱、家务、生育、父母与感情问题，完成后再揭晓分歧。",
    path: "/marriage/questions",
  });

  const applyCode = (value) => setCode(setCoupleCode(value));

  const ensureCode = async () => {
    if (code) return code;
    if (!getUserId()) {
      setHint("请先填写情侣纪念码，或登录后点「用我的账号创建」。");
      return "";
    }
    const data = await ensureCoupleFromAccount();
    applyCode(data.code);
    return data.code;
  };

  useEffect(() => {
    apiJson("/api/marriage/questions").then(({ data }) => setQuestions(data.items || []));
  }, []);

  useEffect(() => {
    if (code) return;
    if (!getUserId()) return;
    ensureCoupleFromAccount()
      .then((data) => applyCode(data.code))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!code) return;
    apiJson(`/api/marriage/questions/${code}?side=${side}`)
      .then(({ data }) => setProgress(data))
      .catch(() => setProgress(null));
  }, [code, side]);

  const answer = async (questionId, text) => {
    try {
      const live = await ensureCode();
      if (!live) return;
      const { res, data } = await apiJson(`/api/marriage/questions/${live}/answers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question_id: questionId, side, answer_text: text }),
      });
      if (!res.ok) throw new Error(data?.detail || "保存失败");
      const next = await apiJson(`/api/marriage/questions/${live}?side=${side}`);
      setProgress(next.data);
      setHint("");
    } catch (err) {
      setHint(err.message || "保存失败");
    }
  };

  const analyze = async () => {
    try {
      const live = await ensureCode();
      if (!live) return;
      const { res, data } = await apiJson(`/api/marriage/questions/${live}/analyze`, { method: "POST" });
      if (!res.ok) throw new Error(data?.detail || "分析失败");
      setReport(data);
    } catch (err) {
      alert(err.message || "分析失败");
    }
  };

  const grouped = questions.reduce((acc, q) => {
    acc[q.category] = acc[q.category] || [];
    acc[q.category].push(q);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-extrabold">💌 婚前100问</h1>
      <p className="text-sm text-gray-500">双方用同一纪念码分别作答，答完才互相可见。不是心理诊断。</p>
      <Disclaimer />
      <CoupleCodeBar code={code} onChange={applyCode} />
      {hint ? <p className="text-sm text-amber-800">{hint}</p> : null}
      <div className="flex gap-2 text-sm">
        <button className={`px-3 py-1 rounded-full ${side === "male" ? "bg-rose-500 text-white" : "border"}`} onClick={() => setSide("male")}>
          男方作答
        </button>
        <button className={`px-3 py-1 rounded-full ${side === "female" ? "bg-pink-500 text-white" : "border"}`} onClick={() => setSide("female")}>
          女方作答
        </button>
        {progress ? (
          <span className="text-gray-500 self-center">
            待完成 {progress.pending_count} 题
          </span>
        ) : null}
      </div>
      {Object.entries(grouped).map(([cat, list]) => (
        <section key={cat} className="space-y-3">
          <h2 className="font-bold text-lg">{cat}</h2>
          {list.map((q) => (
            <div key={q.id} className="bg-white rounded-2xl p-4">
              <p className="mb-2">{q.question_text}</p>
              <div className="flex flex-wrap gap-2">
                {q.options.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => answer(q.id, opt)}
                    className="px-3 py-1.5 text-sm rounded-full border hover:bg-rose-50"
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </section>
      ))}
      <button onClick={analyze} className="px-6 py-3 rounded-xl bg-rose-500 text-white">
        双方完成后查看分歧
      </button>
      {report?.completed ? (
        <div className="bg-white rounded-2xl p-6 space-y-3">
          <p className="font-bold">{report.summary}</p>
          <p>{report.advice}</p>
          {report.diff?.length ? (
            <ul className="text-sm list-disc pl-5">
              {report.diff.map((d) => (
                <li key={d.id}>
                  {d.question}：男方「{d.male}」／女方「{d.female}」
                </li>
              ))}
            </ul>
          ) : null}
          <p className="text-xs text-gray-500">{report.disclaimer}</p>
        </div>
      ) : report ? (
        <p className="text-gray-500">还有题目未答完，答完后才揭晓对方答案。</p>
      ) : null}
    </div>
  );
}
